import { responseRejectInterceptor } from "@api/base";
import { itemApi } from "@api/items";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { Error as IlsError } from "@components/Error";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitInitialQueryState,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import { ItemListEntry } from "@modules/Items/backoffice";
import SearchAggregationsCards from "@modules/SearchControls/SearchAggregationsCards";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import { BackOfficeRoutes } from "@routes/urls";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";
import React, { Component } from "react";
import { OverridableContext } from "react-overridable";
import ClearFiltersButton from "@modules/SearchControls/ClearFiltersButton";
import SessionFiltersPersistence from "@modules/SearchControls/SessionFiltersPersistence";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
  InvenioRequestSerializer,
} from "react-searchkit";
import { Container, Grid, Header } from "semantic-ui-react";
import Qs from "qs";

export class ItemSearch extends Component {
  modelName = "ITEMS";

  searchApi = new InvenioSearchApi({
    axios: {
      url: itemApi.searchBaseURL,
      withCredentials: true,
    },
    interceptors: {
      response: { reject: responseRejectInterceptor },
    },
  });

  handleExportClick = async (format, size, currentQueryState) => {
    const requestSerializer = new InvenioRequestSerializer();
    const newQueryState = { ...currentQueryState, size };
    const queryString = requestSerializer.serialize(newQueryState);
    const params = Qs.parse(queryString);
    params["page"] = 1;

    if (format === "pdf") {
      params["format"] = "csv";
      const args = Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      });
      const fetchUrl = `${itemApi.searchBaseURL}?${args}`;

      try {
        const response = await fetch(fetchUrl, { credentials: "include" });
        const csvText = await response.text();
        this.generatePDFfromCSV(csvText);
      } catch (error) {
        console.error("PDF Export error:", error);
      }
      return;
    }

    params["format"] = format;
    const args = Qs.stringify(params, { arrayFormat: "repeat", encode: false });
    const exportUrl = `${itemApi.searchBaseURL}?${args}`;
    window.open(exportUrl, "_blank");
  };

  generatePDFfromCSV = (csvText) => {
    const reportTitle = "Physical Copies Export Report";
    const date = new Date().toLocaleString();

    // Parse CSV data
    const lines = csvText.split("\n").filter(Boolean);
    const headers = lines[0].split(",").map((h) => h.replace(/"/g, ""));
    const rows = lines.slice(1).map((line) => {
      const cells = line
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
        .map((cell) => cell.replace(/^"|"$/g, "").replace(/""/g, '"'));
      return cells;
    });

    // Generate table headers HTML
    const tableHeaders = headers
      .map((h) => `<th>${this.escapeHtml(h)}</th>`)
      .join("");

    // Generate table rows HTML
    const tableRows = rows
      .map(
        (r) =>
          `<tr>${r
            .map((cell) => `<td>${this.escapeHtml(cell)}</td>`)
            .join("")}</tr>`
      )
      .join("");

    // Use the new template system
    const htmlContent = generateExportHTML({
      reportTitle,
      generatedDate: date,
      tableHeaders,
      tableRows,
      footerText: "Library Management System Export",
      fallbackLogoText: "Library Resource Center",
      showUniversityLogo: true,
      showLrcLogo: true,
    });

    // Create and open the print window
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 2000);

    if (printWindow) {
      printWindow.onload = function () {
        setTimeout(() => {
          printWindow.focus();
        }, 500);
      };
    }
  };

  escapeHtml = (text) => {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  renderError = (error) => {
    return <IlsError error={error} />;
  };

  render() {
    const helperFields = [
      { name: "Barcode", field: "barcode", defaultValue: '"1234567"' },
      { name: "ISBN", field: "isbn", defaultValue: '"1234567"' },
      { name: "Shelf", field: "shelf", defaultValue: '"Cw-Ax-Bs"' },
      { name: "created", field: "_created" },
    ];

    const initialState = setReactSearchKitInitialQueryState(this.modelName);
    const defaultSortingOnEmptyQueryString =
      setReactSearchKitDefaultSortingOnEmptyQueryString(this.modelName);
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);

    return (
      <>
        <Header as="h2">Physical Copies</Header>
        <OverridableContext.Provider
          value={{
            ...SearchControlsOverridesMap,
          }}
        >
          <ReactSearchKit
            searchApi={this.searchApi}
            history={history}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSortingOnEmptyQueryString}
          >
            <>
              <SessionFiltersPersistence moduleIdentifier="items" />
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for physical copies..."
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>

              <Container fluid className="bo-search-body">
                <Grid>
                  <Grid.Row columns={2}>
                    <ResultsLoader>
                      <Grid.Column width={3} className="search-aggregations">
                        <Header content="Filter by" />
                        <ClearFiltersButton moduleIdentifier="items" />
                        <SearchAggregationsCards modelName={this.modelName} />
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Grid columns={2}>
                          <Grid.Column width={8}>
                            <NewButton
                              text="Add Physical Copy"
                              to={BackOfficeRoutes.itemCreate}
                            />
                          </Grid.Column>
                          <Grid.Column width={8} textAlign="right">
                            <ExportReactSearchKitResults
                              exportBaseUrl={itemApi.searchBaseURL}
                              onExportClick={this.handleExportClick}
                              supportedFormats={["csv", "pdf"]}
                            />
                          </Grid.Column>
                        </Grid>
                        <EmptyResults
                          extraContent={
                            <NewButton
                              text="Add item"
                              to={BackOfficeRoutes.itemCreate}
                            />
                          }
                        />
                        <Error />
                        <SearchControls
                          modelName={this.modelName}
                          withLayoutSwitcher={false}
                        />
                        <ResultsList ListEntryElement={ItemListEntry} />
                        <SearchFooter />
                      </Grid.Column>
                    </ResultsLoader>
                  </Grid.Row>
                </Grid>
              </Container>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}
